import * as React from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  IconButton,
  InputAdornment,
  InputAdornmentProps,
  Box,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { Dayjs } from "dayjs";

interface DateInputProps {
  value?: Dayjs | null;
  onChange?: (value: Dayjs | null) => void;
  label?: string; // shown on the left inside the input
  minDate?: Dayjs | null;
  maxDate?: Dayjs | null;
  width?: number; // optional, defaults to 240
  disabled?: boolean;
  /**
   * Show the clear (✕) button. Turn off for required date ranges, where the
   * field must always hold a value and clearing it is meaningless.
   */
  clearable?: boolean;
}

/**
 * Custom end adornment that renders our clear button *alongside* the picker's
 * default calendar open button. In MUI X v6 the open button is supplied through
 * the text field's `InputProps.endAdornment` (the `inputAdornment` slot), so we
 * must keep `children` — overriding `endAdornment` outright would drop it and
 * leave mouse users unable to open the picker.
 */
interface ClearableInputAdornmentProps extends InputAdornmentProps {
  showClear?: boolean;
  onClear?: () => void;
}

const ClearableInputAdornment: React.FC<ClearableInputAdornmentProps> = ({
  children,
  showClear = false,
  onClear,
  ...adornmentProps
}) => {
  // `ownerState` is injected by MUI's slot machinery; destructure it out rather
  // than deleting, so the incoming props object is never mutated.
  const { ownerState: _ownerState, ...cleanProps } = adornmentProps as
    ClearableInputAdornmentProps & { ownerState?: unknown };
  return (
    <InputAdornment {...cleanProps}>
      <IconButton
        size="small"
        aria-label="Clear date"
        disabled={!showClear}
        tabIndex={showClear ? 0 : -1}
        sx={{ visibility: showClear ? "visible" : "hidden" }}
        onMouseDown={(e) => {
          // avoid focus shift that can cause popper reposition/jank
          e.preventDefault();
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClear?.();
        }}
      >
        <ClearIcon fontSize="small" />
      </IconButton>
      {/* the picker's default calendar open button */}
      {children}
    </InputAdornment>
  );
};

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  label = "From",
  minDate,
  maxDate,
  width = 240,
  disabled = false,
  clearable = true,
}) => {
  return (
    <Box>
      <DatePicker
        value={value ?? null}
        onChange={(newValue) => onChange?.(newValue)}
        format="DD-MM-YYYY"
        minDate={minDate ?? undefined}
        maxDate={maxDate ?? undefined}
        disabled={disabled}
        slots={
          // Wrap the default open button so we can render the clear icon next to
          // it instead of replacing the whole end adornment. When not clearable,
          // leave the slot alone so the picker keeps its stock adornment.
          clearable ? { inputAdornment: ClearableInputAdornment } : undefined
        }
        slotProps={{
          // Leave the popper portalled to document.body (MUI's default). This is
          // a shared input that may be used inside Dialogs / scrollable panels,
          // where disablePortal would let an `overflow: hidden` ancestor clip the
          // calendar or push it behind a modal backdrop. If reposition "jump"
          // recurs in a specific layout, prefer popper modifiers over disablePortal.
          ...(clearable
            ? {
              inputAdornment: {
                sx: { marginRight: 0 },
                showClear: Boolean(value) && !disabled,
                onClear: () => onChange?.(null),
              } as ClearableInputAdornmentProps,
            }
            : {}),
          textField: {
            sx: {
              width,
              "& .MuiInputBase-root": {
                height: 57,
                paddingLeft: "10px",
              },
              "& .MuiInputBase-input": {
                padding: "10px 0",
                fontSize: "15px",
              },
            },
            // prevent MUI helper text from pushing layout (and triggering popper re-position)
            error: false,
            helperText: undefined,
            InputProps: {
              // Left label inside input
              startAdornment: (
                <InputAdornment position="start">{label}</InputAdornment>
              ),
            },
          },
        }}
      />
    </Box>
  );
};

export default DateInput;